import React, { useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { stepPropTypes, stepDefaultProps } from './stepProps';
import {
  createVariant, updateVariant, deleteVariant, reorderVariants,
} from '../api';

// The purchase choices -- what a customer actually clicks. Part of the merged
// contents step (see Contents.jsx); no longer a step of its own.
//
// The common case is one per print-package option, so that is a button rather
// than a form. Each row says whether its prints would really reach the printer:
// a print with no template charges the customer and prints nothing.
// Real options repeat the same print many times -- "Buy All Your Photos" carries one
// `(1) High-Resolution Downloads` row per pose, 26 of them, which listed line by
// line filled the screen and said nothing. Identical names collapse to a count, and
// a print that would never print keeps its own line so the warning is not hidden
// inside a tally.
const groupPrints = (prints) => {
  const groups = [];
  prints.forEach((print) => {
    const key = `${print.name}|${print.print_ready}`;
    const existing = groups.find((g) => g.key === key);
    if (existing) existing.count += 1;
    else groups.push({ ...print, key, count: 1 });
  });
  return groups;
};

// The print-package options this product does not sell yet, named so the admin can
// see whether the omission was deliberate.
const uncoveredMessage = (uncovered, packageName) => {
  const one = uncovered.length === 1;
  const names = uncovered.map((o) => o.name).join(', ');

  return `${uncovered.length} package${one ? '' : 's'} in ${packageName} ${one ? 'is' : 'are'} `
    + `not offered yet: ${names}`;
};

// Why a quantity starts where it does, in the admin's terms: wallets come 8 to a
// printed sheet, a download is not printed at all, and a line with no print
// template cannot be sold however much anyone wants it.
const lineHelp = (line) => {
  if (!line.available) return 'No print template — unavailable';
  if (line.digital) return 'Not printed or shipped';

  return `${line.per_sheet} per printed sheet`;
};

// Move one row of a list to another index. Pure, because it is the whole of what a
// drag MEANS -- the drag mechanics themselves are DOM events that jsdom cannot
// faithfully replay, so this is the part that gets tested.
export const moveItem = (list, from, to) => {
  if (from === to || from < 0 || to < 0 || from >= list.length || to >= list.length) return list;
  const next = [...list];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
};

// How each sortable column compares. Poses is a NUMBER -- sorted as text, 25 lands
// between 2 and 8 -- and "prints" means how many a package sells, which is the
// question being asked when you sort by it.
const SORTS = {
  name: (v) => (v.name || '').toLowerCase(),
  poses: (v) => Number(v.poses) || 0,
  prints: (v) => (v.prints || []).length,
};

const sortRows = (rows, key, dir) => {
  const read = SORTS[key];
  if (!read) return rows;

  return [...rows].sort((a, b) => {
    const left = read(a);
    const right = read(b);
    if (left === right) return 0;

    return (left < right ? -1 : 1) * (dir === 'desc' ? -1 : 1);
  });
};

const extraName = (extras, id) => {
  const found = (extras || []).find((e) => e.id === id);

  return found ? `${found.group} · ${found.name}` : `Extra ${id}`;
};

const Packages = ({
  product, choices, commit, busy, reload,
}) => {
  const [adding, setAdding] = useState({
    name: '', poses: 1, legacy_option_id: '', legacy_extra_id: '',
  });
  const [building, setBuilding] = useState({ name: '', poses: 1, lines: {} });
  // Which package is open for editing, and the values being typed into it. Editing
  // lives here because this step is the only place packages are managed now -- the
  // one-page editor that used to own it is gone (#5446).
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({});
  // A column to sort by, or null for the order customers actually see. Sorting here
  // is for FINDING a package in a list of 14-26; it deliberately does not save,
  // because this table's order is a customer-facing setting (Shop::ProductSerializer
  // reads `variants.active.positioned`). Dragging is what changes that.
  const [sort, setSort] = useState({ key: null, dir: 'asc' });
  const [dragFrom, setDragFrom] = useState(null);
  const sorted = sort.key !== null;

  const toggleSort = (key) => setSort((s) => (
    s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }
  ));

  // The server sends them in position order, so "no sort" is the customer's order.
  const rows = sorted ? sortRows(product.variants, sort.key, sort.dir) : product.variants;

  // Dragging is disabled while sorted -- dropping a row into a list ordered by name
  // has no meaning for the order shoppers see, and guessing one would write an order
  // nobody chose.
  const dropOn = async (to) => {
    if (dragFrom === null || sorted) return;

    const next = moveItem(product.variants, dragFrom, to);
    setDragFrom(null);
    if (next === product.variants) return;

    await reorderVariants(product.id, next.map((v) => v.id));
    reload();
  };

  // A personalised product is a SET: its packages are the items, and each item is its
  // own Printess design. The product-level template is only the fallback for a
  // single-item product (Shop::ProductVariant#printess_template_id).
  const isPrintess = product.product_type === 'printess';

  const startEdit = (variant) => {
    setEditing(variant.id);
    setDraft({
      name: variant.name || '',
      poses: variant.poses ?? 1,
      additional_pose_charge: variant.additional_pose_charge ?? '',
      price: variant.price ?? '',
      best_value: !!variant.badges?.best_value,
      super_value: !!variant.badges?.super_value,
      digital_images_only: !!variant.badges?.digital_images_only,
      active: variant.active !== false,
      printess_template_id: variant.printess?.own || '',
    });
  };

  const saveEdit = async () => {
    // Booleans as '1'/'0': the endpoint is the same one a checkbox form posts to,
    // and Rails casts those the same either way.
    const { printess_template_id: printessTemplate, ...attrs } = draft;
    await updateVariant(
      product.id,
      editing,
      {
        ...attrs,
        best_value: draft.best_value ? '1' : '0',
        super_value: draft.super_value ? '1' : '0',
        digital_images_only: draft.digital_images_only ? '1' : '0',
        active: draft.active ? '1' : '0',
      },
      // Top level, not inside the variant params: it is stored on metadata and the
      // controller writes it explicitly. Blank clears it, so the item goes back to
      // inheriting the product's design rather than carrying an empty name.
      isPrintess ? { printess_template_id: printessTemplate } : {},
    );
    setEditing(null);
    reload();
  };

  // The editor renders directly UNDER the row being changed, not at the foot of the
  // table: with it at the foot, editing the first of several packages put the form
  // several rows away from the name it was editing, and the two read as unrelated.
  // Every field here was previously only on the one-page editor, and three of them
  // reach the STOREFRONT: the badges become ProductVariant#badge, and digital only
  // decides whether the order ships.
  const editorRow = () => (
    <TableRow>
      <TableCell colSpan={5} sx={{ bgcolor: 'action.hover' }}>
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center">
          <TextField
            label="Name customers see"
            size="small"
            value={draft.name}
            sx={{ width: 240 }}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          />
          <TextField
            label="Poses included"
            type="number"
            size="small"
            value={draft.poses}
            sx={{ width: 130 }}
            InputProps={{ inputProps: { min: 1 } }}
            onChange={(e) => setDraft({ ...draft, poses: e.target.value })}
          />
          <TextField
            label="Per extra pose"
            type="number"
            size="small"
            value={draft.additional_pose_charge}
            sx={{ width: 140 }}
            InputProps={{ inputProps: { min: 0, step: '0.01' } }}
            onChange={(e) => setDraft({ ...draft, additional_pose_charge: e.target.value })}
          />
          <TextField
            label="Price"
            type="number"
            size="small"
            value={draft.price}
            sx={{ width: 120 }}
            helperText="Blank uses the pricelist"
            InputProps={{ inputProps: { min: 0, step: '0.01' } }}
            onChange={(e) => setDraft({ ...draft, price: e.target.value })}
          />
          {/* Each item in a personalised set is its OWN design -- the mug and
                        the magnet are different templates -- so the template belongs on
                        the item, not on the product. */}
          {isPrintess ? (
            <TextField
              label="Printess design"
              size="small"
              value={draft.printess_template_id}
              sx={{ width: 240 }}
                        // The product-level design is a fallback carried by older
                        // products; nothing sets it now (#5447), so on most products
                        // blank means this item has no design at all -- and one item
                        // without one costs the WHOLE product its place in the store.
              helperText={product.printess_template_id
                ? `Blank uses the product's design (${product.printess_template_id})`
                : 'Needed — without it the store leaves this product out'}
              onChange={(e) => setDraft({ ...draft, printess_template_id: e.target.value })}
            />
          ) : null}
        </Stack>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
          {[['best_value', 'Best value'], ['super_value', 'Most popular'],
            ['digital_images_only', 'Digital only'], ['active', 'Offered']].map(([key, label]) => (
              <FormControlLabel
                key={key}
                sx={{ display: 'flex' }}
                control={(
                  <Checkbox
                    checked={!!draft[key]}
                    onChange={(e) => setDraft({ ...draft, [key]: e.target.checked })}
                  />
                          )}
                label={<Typography variant="body2">{label}</Typography>}
              />
          ))}
          <Button variant="contained" disabled={busy} onClick={saveEdit}>Save package</Button>
          <Button disabled={busy} onClick={() => setEditing(null)}>Cancel</Button>
        </Stack>
      </TableCell>
    </TableRow>
  );

  const covered = new Set(product.variants.map((v) => v.legacy_option_id).filter(Boolean));
  const uncovered = choices.package_options.filter((o) => !covered.has(o.id));
  const printLines = choices.print_lines || [];
  const presets = choices.presets || [];
  const importable = choices.importable || [];
  const [presetId, setPresetId] = useState('');
  const [source, setSource] = useState({ productId: '', optionId: '' });
  // Nothing to link to means the pricelist was just created empty, so the only way
  // forward is to say what is IN the package -- default to that rather than showing
  // an empty dropdown. A preset beats both: it is the whole point that the same
  // package is not rebuilt by hand each season (#5417).
  const canImport = importable.length > 0;
  const firstMode = () => {
    if (presets.length) return 'preset';
    return canImport ? 'import' : 'build';
  };
  const [mode, setMode] = useState(firstMode);
  const chosenPreset = presets.find((p) => String(p.id) === String(presetId)) || null;

  const add = async () => {
    if (!adding.name) return;
    await createVariant(product.id, adding);
    setAdding({
      name: '', poses: 1, legacy_option_id: '', legacy_extra_id: '',
    });
    reload();
  };

  const remove = async (variant) => {
    /* eslint-disable-next-line no-alert */
    if (!window.confirm(`Remove ${variant.name}?`)) return;
    await deleteVariant(product.id, variant.id);
    reload();
  };

  const chosenLines = Object.entries(building.lines)
    .filter(([, quantity]) => quantity)
    .map(([key, quantity]) => ({ key, quantity }));

  // Cleared only once the server has accepted it. Clearing first loses what was
  // typed on exactly the occasions the admin needs it back -- a refused name, or a
  // print line the catalogue would not build.
  const build = async () => {
    const ok = await commit({
      buy_option: { name: building.name, poses: building.poses, lines: chosenLines },
    });
    if (ok) setBuilding({ name: '', poses: 1, lines: {} });
  };

  // The whole package comes from the definition -- name, poses, extra-pose charge
  // and prints -- so there is nothing else to fill in here. It stays editable
  // afterwards like any other package.
  const addPreset = async () => {
    const ok = await commit({ buy_option: { preset_id: String(presetId) } });
    if (ok) setPresetId('');
  };

  // Copied, not linked: the rows are duplicated into THIS pricelist, so the source
  // can change later without touching what we just sold.
  const importPackage = async () => {
    const ok = await commit({ buy_option: { import_option_id: String(source.optionId) } });
    if (ok) setSource({ productId: '', optionId: '' });
  };

  const sourceProduct = importable.find((p) => String(p.id) === String(source.productId)) || null;

  if (product.product_type === 'addon') {
    return (
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          {'An add-on attaches to something already in the cart rather than being bought '
            + 'on its own, so it has no prints and no poses. Each package points at the '
            + 'legacy add-on item (its Extra) that the order actually records.'}
        </Typography>

        {product.variants.length ? (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name customers see</TableCell>
                <TableCell>Add-on item it applies</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {product.variants.map((v) => (
                <TableRow key={v.id}>
                  <TableCell>{v.name}</TableCell>
                  <TableCell>
                    {v.legacy_extra_id ? (
                      <Typography variant="caption" color="text.secondary">
                        {extraName(choices.extras, v.legacy_extra_id)}
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="error.main">
                        Nothing linked — the store refuses to attach this
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Button color="error" disabled={busy} onClick={() => remove(v)}>Remove</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Typography variant="body2" color="text.secondary">No packages yet.</Typography>
        )}

        <Divider />

        <Box>
          <Typography variant="subtitle2" gutterBottom>Add a package</Typography>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="flex-start">
            <TextField
              label="Name customers see"
              value={adding.name}
              sx={{ flex: '1 1 220px' }}
              onChange={(e) => setAdding({ ...adding, name: e.target.value })}
            />
            <TextField
              select
              label="Add-on item it applies"
              value={adding.legacy_extra_id || ''}
              sx={{ flex: '1 1 300px' }}
              helperText="The legacy Extra the order records. Grouped by add-on type."
              onChange={(e) => setAdding({ ...adding, legacy_extra_id: e.target.value })}
            >
              <MenuItem value="">— choose an add-on item —</MenuItem>
              {(choices.extras || []).map((extra) => (
                <MenuItem key={extra.id} value={extra.id}>
                  {`${extra.group} · ${extra.name}`}
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant="contained"
              sx={{ mt: 0.5 }}
              disabled={busy || !adding.name.trim() || !adding.legacy_extra_id}
              onClick={add}
            >
              Add
            </Button>
          </Stack>
        </Box>
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {product.product_type === 'package'
          ? 'One purchasable choice each: its name, how many poses it includes, and what it '
            + 'costs. A choice can hold several prints — two 8×10s and eight wallets, say.'
          : 'One purchasable choice, holding the single item the customer receives. '
            + 'Prices come next.'}
      </Typography>

      {/* Says so plainly, because the consequence of getting it wrong is invisible
          here and visible to every shopper. */}
      {product.variants.length > 1 ? (
        <Alert
          severity={sorted ? 'warning' : 'info'}
          sx={{ mb: 1 }}
          action={sorted ? (
            <Button color="inherit" size="small" onClick={() => setSort({ key: null, dir: 'asc' })}>
              Show customer order
            </Button>
          ) : null}
        >
          {sorted
            ? 'Sorted for looking — this is NOT the order customers see, and dragging is off.'
            : 'This is the order customers see. Drag a row by its handle to change it.'}
        </Alert>
      ) : null}

      {product.variants.length ? (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 32 }} />
              <TableCell sortDirection={sort.key === 'name' ? sort.dir : false}>
                <TableSortLabel
                  active={sort.key === 'name'}
                  direction={sort.key === 'name' ? sort.dir : 'asc'}
                  onClick={() => toggleSort('name')}
                >
                  Name customers see
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sort.key === 'poses' ? sort.dir : false}>
                <TableSortLabel
                  active={sort.key === 'poses'}
                  direction={sort.key === 'poses' ? sort.dir : 'asc'}
                  onClick={() => toggleSort('poses')}
                >
                  Poses
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sort.key === 'prints' ? sort.dir : false}>
                <TableSortLabel
                  active={sort.key === 'prints'}
                  direction={sort.key === 'prints' ? sort.dir : 'asc'}
                  onClick={() => toggleSort('prints')}
                >
                  {isPrintess ? 'What they get' : 'Prints it sells'}
                </TableSortLabel>
              </TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((v, index) => (
              <React.Fragment key={v.id}>
                <TableRow
                  selected={editing === v.id}
                  draggable={!sorted && !busy}
                  onDragStart={() => setDragFrom(index)}
                  onDragOver={(e) => { if (!sorted) e.preventDefault(); }}
                  onDrop={() => dropOn(index)}
                  onDragEnd={() => setDragFrom(null)}
                  sx={dragFrom === index ? { opacity: 0.4 } : undefined}
                >
                  <TableCell
                    sx={{ cursor: sorted ? 'not-allowed' : 'grab', color: 'text.disabled', userSelect: 'none' }}
                    aria-label={sorted ? 'Reordering is off while sorted' : `Drag ${v.name} to reorder`}
                  >
                    ⠿
                  </TableCell>
                  <TableCell>
                    {v.name}
                    {/* Badges reach the STOREFRONT (ProductVariant#badge), so they are
                      shown on the row rather than only inside the editor. */}
                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.25 }}>
                      {v.badges?.best_value ? <Chip size="small" label="Best value" /> : null}
                      {v.badges?.super_value ? <Chip size="small" label="Most popular" /> : null}
                      {v.badges?.digital_images_only ? <Chip size="small" label="Digital only" /> : null}
                      {v.active === false ? <Chip size="small" color="warning" label="off" /> : null}
                    </Stack>
                    {isPrintess ? (
                      <Typography
                        variant="caption"
                        display="block"
                        color={v.printess?.resolved ? 'text.secondary' : 'error.main'}
                      >
                        {v.printess?.resolved
                          ? `${v.printess.resolved}${v.printess.own ? '' : " (product's)"}`
                          : 'No Printess design — the store leaves this item out'}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>{v.poses}</TableCell>
                  <TableCell>
                    {v.prints.length ? groupPrints(v.prints).map((pr) => (
                      <Typography
                        key={pr.key}
                        variant="caption"
                        display="block"
                        color={pr.print_ready ? 'text.secondary' : 'error.main'}
                      >
                        {pr.count > 1 ? `${pr.count} × ` : ''}
                        {pr.name || '(unnamed print)'}
                        {pr.print_ready ? null : ' — no print template, would never print'}
                      </Typography>
                    )) : (
                      <Typography
                        variant="caption"
                        color={isPrintess ? 'text.secondary' : 'error.main'}
                      >
                        {/* A personalised item HAS no prints -- the customer designs it
                          and the order line carries no sheets (CartBuilder#add_printess)
                          -- so the warning that fits every other kind is simply wrong
                          here, and read as "this is broken" on a set that is fine. */}
                        {isPrintess
                          ? 'No prints — the customer designs this one'
                          : 'Nothing linked — customers cannot buy this'}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Button disabled={busy} onClick={() => startEdit(v)}>Edit</Button>
                    <Button color="error" disabled={busy} onClick={() => remove(v)}>Remove</Button>
                  </TableCell>
                </TableRow>
                {editing === v.id ? editorRow() : null}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Typography variant="body2" color="text.secondary">No packages yet.</Typography>
      )}

      {uncovered.length ? (
        <Alert
          severity="info"
          action={(
            <Button color="inherit" disabled={busy} onClick={() => commit({ seed_options: '1' })}>
              Add these
            </Button>
          )}
        >
          {uncoveredMessage(uncovered, product.legacy_package_name)}
        </Alert>
      ) : null}

      <Divider />

      <Box>
        <Typography variant="subtitle2" gutterBottom>Add a package</Typography>

        {printLines.length ? (
          <RadioGroup row value={mode} onChange={(e) => setMode(e.target.value)}>
            {presets.length ? (
              <FormControlLabel
                value="preset"
                control={<Radio size="small" />}
                label={<Typography variant="body2">Start from a preset</Typography>}
              />
            ) : null}
            <FormControlLabel
              value="import"
              disabled={!canImport}
              control={<Radio size="small" />}
              label={(
                <Typography variant="body2">
                  Copy from another pricelist
                  {canImport ? '' : ' — there are none to copy from'}
                </Typography>
              )}
            />
            <FormControlLabel
              value="build"
              control={<Radio size="small" />}
              label={<Typography variant="body2">Choose the prints myself</Typography>}
            />
          </RadioGroup>
        ) : null}

        {mode === 'preset' && presets.length ? (
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="flex-start">
              <TextField
                select
                label="Which package?"
                value={presetId}
                sx={{ flex: '1 1 320px' }}
                disabled={busy}
                onChange={(e) => setPresetId(e.target.value)}
              >
                {presets.map((preset) => (
                  <MenuItem key={preset.id} value={preset.id}>
                    {`${preset.name} — ${preset.summary}`}
                    {preset.already_used ? ' · already in this pricelist' : ''}
                  </MenuItem>
                ))}
              </TextField>
              <Button
                variant="contained"
                sx={{ mt: 1 }}
                disabled={busy || !presetId || (chosenPreset && !chosenPreset.buildable)}
                onClick={addPreset}
              >
                Add this package
              </Button>
            </Stack>

            {/* Resolved on the server, because it depends on PrintTemplate rows the
                browser cannot see. Adding it anyway would create an option whose
                prints never reach the printer (#5364), so the server refuses it
                too -- this is only so the admin is not told after the click. */}
            {chosenPreset && !chosenPreset.buildable ? (
              <Alert severity="error">
                {`“${chosenPreset.name}” has a print with no print template, so nothing would `
                  + 'reach the printer. Fix the print template first, or build this package by hand.'}
              </Alert>
            ) : null}

            {chosenPreset && chosenPreset.buildable ? (
              <Typography variant="caption" color="text.secondary">
                {`Adds ${chosenPreset.summary}, ${chosenPreset.poses} pose`
                  + `${chosenPreset.poses === 1 ? '' : 's'}`
                  + `${chosenPreset.additional_pose_charge
                    ? `, $${chosenPreset.additional_pose_charge} per extra pose` : ''}`
                  + '. Everything stays editable afterwards, and the price is set on the next step.'}
              </Typography>
            ) : null}
          </Stack>
        ) : null}

        {/* Copied, never linked. The old "sell prints already in this pricelist" sold a
            legacy Option someone had filled in elsewhere; this duplicates the rows into
            THIS pricelist, so nothing is shared and the source can change freely
            afterwards. It also carries the 26 print names the curated catalogue cannot
            express (#5422), which a preset cannot. */}
        {mode === 'import' && canImport ? (
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="flex-start">
              <TextField
                select
                label="From which pricelist?"
                value={source.productId}
                sx={{ flex: '1 1 240px' }}
                disabled={busy}
                onChange={(e) => setSource({ productId: e.target.value, optionId: '' })}
              >
                {importable.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {`${p.name}${p.season ? ` · ${p.season}` : ''}`}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Which package?"
                value={source.optionId}
                sx={{ flex: '1 1 300px' }}
                disabled={busy || !sourceProduct}
                onChange={(e) => setSource({ ...source, optionId: e.target.value })}
              >
                {(sourceProduct ? sourceProduct.packages : []).map((pkg) => (
                  <MenuItem key={pkg.option_id} value={pkg.option_id}>
                    {`${pkg.name} — ${pkg.summary}`}
                  </MenuItem>
                ))}
              </TextField>
              <Button
                variant="contained"
                sx={{ mt: 1 }}
                disabled={busy || !source.optionId}
                onClick={importPackage}
              >
                Copy this package
              </Button>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {'Copies the name, poses and prints into this pricelist as a new package. '
                + 'The price is set on the next step, and nothing about the original changes.'}
            </Typography>
          </Stack>
        ) : null}

        {mode === 'build' && printLines.length ? (
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              <TextField
                label="Name customers see"
                value={building.name}
                sx={{ flex: '1 1 220px' }}
                onChange={(e) => setBuilding({ ...building, name: e.target.value })}
                helperText="e.g. Basic, Deluxe, All Digital"
              />
              <TextField
                label="Poses"
                type="number"
                value={building.poses}
                sx={{ width: 110 }}
                InputProps={{ inputProps: { min: 1 } }}
                onChange={(e) => setBuilding({ ...building, poses: e.target.value })}
                helperText="Different photos"
              />
            </Stack>

            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {printLines.map((line) => (
                <TextField
                  key={line.key}
                  select
                  size="small"
                  label={line.label}
                  disabled={!line.available}
                  value={building.lines[line.key] || ''}
                  sx={{ width: 170 }}
                  onChange={(e) => setBuilding({
                    ...building,
                    lines: { ...building.lines, [line.key]: e.target.value },
                  })}
                  helperText={lineHelp(line)}
                >
                  <MenuItem value="">none</MenuItem>
                  {line.quantities.map((q) => (
                    <MenuItem key={q} value={q}>{`${q}`}</MenuItem>
                  ))}
                </TextField>
              ))}
            </Stack>

            <Box>
              <Button
                variant="contained"
                disabled={busy || !building.name.trim() || !chosenLines.length}
                onClick={build}
              >
                Add this package
              </Button>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Quantities are the ones the printer can actually produce, so the sheets always
                divide evenly. Names and print templates are filled in for you.
              </Typography>
            </Box>
          </Stack>
        ) : null}
      </Box>
    </Stack>
  );
};

Packages.propTypes = stepPropTypes;
Packages.defaultProps = stepDefaultProps;

export default Packages;
